import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, type Habilitation, type UserSummary } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { formatDate, KIND_LABELS, STATUS_LABELS, STATUS_TONE } from '../labels';
import { Icon } from '../components/icons';
import { useReferentials } from './Workspace';
import '../styles/compte.css';
import '../styles/habilitations.css';

function ErrorBox({ error }: { error: ApiError | null }) {
  if (!error) return null;
  return (
    <div className="alert alert-danger" role="alert">
      {error.message}
      {error.errors.length > 0 && (
        <ul className="small" style={{ margin: '6px 0 0' }}>
          {error.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusBadge({ h }: { h: Habilitation }) {
  return (
    <span className="hab-status">
      <span className={`badge badge-${STATUS_TONE[h.status]}`}>{STATUS_LABELS[h.status]}</span>
      {h.isEmergency && <span className="badge badge-warn">Urgence</span>}
      {h.reviewRequired && <span className="badge badge-danger">À contrôler</span>}
    </span>
  );
}

const FILTERS = [
  { value: '', label: 'Toutes' },
  { value: 'demandee', label: 'À décider' },
  { value: 'active', label: 'Actives' },
  { value: 'suspendue', label: 'Suspendues' },
  { value: 'urgence_a_controler', label: 'Urgences à contrôler' },
  { value: 'retiree', label: 'Retirées' },
  { value: 'refusee', label: 'Refusées' },
];

export function HabilitationsPage() {
  const { me } = useAuth();
  const refs = useReferentials();
  const [params, setParams] = useSearchParams();
  const filter = params.get('statut') ?? 'demandee';
  const [items, setItems] = useState<Habilitation[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const can = (p: string) => me?.permissions.includes(p) ?? false;

  const canConsult = me?.permissions.includes('habilitation.consulter') ?? false;
  useEffect(() => {
    if (!canConsult) return;
    setItems(null);
    api<Habilitation[]>(`/habilitations${filter ? `?statut=${filter}` : ''}`)
      .then(setItems)
      .catch(setError);
  }, [filter, canConsult]);

  const profileLabel = (code: string) => refs?.profiles.find((p) => p.code === code)?.label ?? code;

  return (
    <div className="stack">
      <div className="head hab-head">
        <div>
          <h1>Habilitations</h1>
          <p className="lead">
            Une habilitation combine un profil, un organisme, une compétence, des finalités, un niveau de
            sensibilité et une durée. Elle est demandée avec un motif et approuvée par une autre personne.
          </p>
        </div>
        <div className="actions">
          {can('habilitation.demander') && (
            <Link className="btn" to="/espace/habilitations/nouvelle">
              Nouvelle demande
            </Link>
          )}
          {can('habilitation.approuver') && (
            <Link className="btn btn-gold" to="/espace/habilitations/urgence">
              Accès d’urgence
            </Link>
          )}
          {can('utilisateur.gerer') && (
            <Link className="btn btn-ghost" to="/espace/habilitations/utilisateurs">
              Utilisateurs
            </Link>
          )}
        </div>
      </div>

      {!can('habilitation.consulter') ? (
        <div className="alert alert-info">
          Votre profil permet de demander des habilitations, pas de les consulter. Vos demandes apparaissent
          dans votre espace une fois approuvées.
        </div>
      ) : (
        <>
          <div className="tabs hab-filters" role="group" aria-label="Filtrer par statut">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                className="tab hab-tab"
                aria-pressed={filter === f.value}
                onClick={() => setParams(f.value ? { statut: f.value } : { statut: '' })}
              >
                {f.label}
              </button>
            ))}
          </div>
          <ErrorBox error={error} />
          {items === null ? (
            <section className="card">
              <div className="empty">Chargement…</div>
            </section>
          ) : items.length === 0 ? (
            <section className="card">
              <div className="empty">
                <span className="ic" aria-hidden="true">
                  <Icon name="key" size={22} />
                </span>
                <b>Aucune habilitation dans cette catégorie.</b>
              </div>
            </section>
          ) : (
            <section className="card hab-list">
              <div className="table-wrap tbl-inset">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Bénéficiaire</th>
                      <th scope="col">Profil</th>
                      <th scope="col">Organisme</th>
                      <th scope="col">Statut</th>
                      <th scope="col">Échéance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((h) => (
                      <tr key={h.id}>
                        <td data-l="Bénéficiaire">
                          <Link to={`/espace/habilitations/${h.id}`}>{h.userDisplayName}</Link>
                        </td>
                        <td data-l="Profil">{profileLabel(h.profileCode)}</td>
                        <td data-l="Organisme">{h.organizationName}</td>
                        <td data-l="Statut">
                          <StatusBadge h={h} />
                        </td>
                        <td data-l="Échéance">{formatDate(h.validUntil)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

const HISTORY_LABELS: Record<string, string> = {
  demandee: 'Demandée',
  approuvee: 'Approuvée',
  refusee: 'Refusée',
  suspendue: 'Suspendue',
  retiree: 'Retirée',
  urgence_accordee: 'Accès d’urgence accordé',
  controlee: 'Contrôlée a posteriori',
};

interface HistoryItem {
  action: string;
  reason: string;
  occurred_at: string;
  actor: string;
}

const ACTIONS: {
  key: 'approuver' | 'refuser' | 'suspendre' | 'retirer';
  label: string;
  from: string[];
  tone: string;
}[] = [
  { key: 'approuver', label: 'Approuver', from: ['demandee', 'suspendue'], tone: '' },
  { key: 'refuser', label: 'Refuser', from: ['demandee'], tone: 'btn-danger' },
  { key: 'suspendre', label: 'Suspendre', from: ['active', 'a_venir'], tone: 'btn-ghost' },
  {
    key: 'retirer',
    label: 'Retirer',
    from: ['active', 'suspendue', 'a_venir', 'expiree'],
    tone: 'btn-danger',
  },
];

export function HabilitationDetailPage() {
  const { id } = useParams();
  const { me, refreshMe } = useAuth();
  const refs = useReferentials();
  const [h, setH] = useState<Habilitation | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [hab, hist] = await Promise.all([
        api<Habilitation>(`/habilitations/${id}`),
        api<HistoryItem[]>(`/habilitations/${id}/historique`),
      ]);
      setH(hab);
      setHistory(hist);
    } catch (e) {
      setError(e as ApiError);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (action: string) => {
    setError(null);
    setNotice(null);
    try {
      const path = action === 'controler' ? 'controler' : action;
      await api(`/habilitations/${id}/${path}`, { method: 'POST', body: { reason } });
      setReason('');
      setNotice('Décision enregistrée.');
      await load();
      await refreshMe();
    } catch (e) {
      setError(e as ApiError);
    }
  };

  if (!h) return error ? <ErrorBox error={error} /> : <p>Chargement…</p>;
  const canDecide = me?.permissions.includes('habilitation.approuver') ?? false;
  const profile = refs?.profiles.find((p) => p.code === h.profileCode);
  const jurName = (jid: string) => refs?.jurisdictions.find((j) => j.id === jid)?.name ?? jid;
  const purposeLabel = (c: string) => refs?.purposes.find((p) => p.code === c)?.label ?? c;
  const sensitivity =
    refs?.sensitivities.find((s) => s.level === h.maxSensitivity)?.label ?? h.maxSensitivity;
  const available = ACTIONS.filter((a) => a.from.includes(h.status));

  return (
    <div className="stack">
      <div className="head hab-head">
        <div>
          <p className="crumb">
            <Link to="/espace/habilitations">← Habilitations</Link>
          </p>
          <h1>{h.userDisplayName}</h1>
        </div>
        <StatusBadge h={h} />
      </div>
      <div className="cols hab-detail-cols">
        <section className="card hab-card">
          <h2>Détail</h2>
          <dl className="details">
            <dt>Profil</dt>
            <dd>
              {profile?.label ?? h.profileCode}
              {profile && <div className="muted small">{profile.restriction}</div>}
            </dd>
            <dt>Organisme</dt>
            <dd>{h.organizationName}</dd>
            <dt>Compétence</dt>
            <dd>{h.scope === 'national' ? 'Nationale' : h.jurisdictionIds.map(jurName).join(', ')}</dd>
            <dt>Finalités</dt>
            <dd>{h.purposes.length ? h.purposes.map(purposeLabel).join(', ') : '-'}</dd>
            <dt>Sensibilité max.</dt>
            <dd>{sensitivity}</dd>
            <dt>Période</dt>
            <dd>
              du {formatDate(h.validFrom)} au {formatDate(h.validUntil)}
            </dd>
            <dt>Motif</dt>
            <dd>{h.requestReason}</dd>
          </dl>
        </section>
        <section className="card hab-card">
          <h2>Historique</h2>
          <ol className="tl">
            {history.map((e, i) => (
              <li key={i} className={i === history.length - 1 ? 'now' : 'done'}>
                <b>
                  {HISTORY_LABELS[e.action] ?? e.action} - {e.actor}
                </b>
                <span>{formatDate(e.occurred_at)}</span>
                <div className="small">{e.reason}</div>
              </li>
            ))}
          </ol>
        </section>
      </div>

      {canDecide && (available.length > 0 || h.reviewRequired) && (
        <section className="card hab-card">
          <h2>Décision</h2>
          <p className="muted small">
            Vous ne pouvez pas décider d’une habilitation qui vous concerne ou que vous avez demandée : le
            serveur le refusera.
          </p>
          <div className="field">
            <label htmlFor="reason">Motif (obligatoire)</label>
            <textarea id="reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <ErrorBox error={error} />
          {notice && (
            <p className="alert alert-ok" role="status">
              {notice}
            </p>
          )}
          <div className="actions hab-actions">
            {available.map((a) => (
              <button
                key={a.key}
                type="button"
                className={`btn ${a.tone}`}
                disabled={reason.trim().length < 5}
                onClick={() => void act(a.key)}
              >
                {a.label}
              </button>
            ))}
            {h.reviewRequired && (
              <button
                type="button"
                className="btn btn-gold"
                disabled={reason.trim().length < 5}
                onClick={() => void act('controler')}
              >
                Contrôler l’accès d’urgence
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function HabilitationFormPage({ emergency = false }: { emergency?: boolean }) {
  const refs = useReferentials();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    userId: '',
    profileCode: '',
    organizationId: '',
    scope: 'juridictions' as 'national' | 'juridictions',
    jurisdictionIds: [] as string[],
    purposes: [] as string[],
    maxSensitivity: 0,
    validFrom: toLocalInput(new Date()),
    validUntil: toLocalInput(new Date(Date.now() + 90 * 86_400_000)),
    durationMinutes: 120,
    reason: '',
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api<UserSummary[]>('/utilisateurs')
      .then(setUsers)
      .catch(() => setUsers([]));
  }, []);

  const profile = useMemo(
    () => refs?.profiles.find((p) => p.code === form.profileCode),
    [refs, form.profileCode],
  );
  const toggle = (key: 'jurisdictionIds' | 'purposes', value: string) =>
    set(key, form[key].includes(value) ? form[key].filter((v) => v !== value) : [...form[key], value]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const common = {
      userId: form.userId,
      profileCode: form.profileCode,
      organizationId: form.organizationId,
      scope: form.scope,
      jurisdictionIds: form.scope === 'juridictions' ? form.jurisdictionIds : [],
      purposes: form.purposes,
      maxSensitivity: Number(form.maxSensitivity),
      reason: form.reason,
    };
    try {
      const created = await api<Habilitation>(emergency ? '/habilitations/urgence' : '/habilitations', {
        method: 'POST',
        body: emergency
          ? { ...common, durationMinutes: Number(form.durationMinutes) }
          : {
              ...common,
              validFrom: new Date(form.validFrom).toISOString(),
              validUntil: new Date(form.validUntil).toISOString(),
            },
      });
      navigate(`/espace/habilitations/${created.id}`);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  };

  if (!refs) return <p>Chargement…</p>;
  return (
    <div className="stack" style={{ maxWidth: 900 }}>
      <div className="head hab-head">
        <div>
          <p className="crumb">
            <Link to="/espace/habilitations">← Habilitations</Link>
          </p>
          <h1>{emergency ? 'Accorder un accès d’urgence' : 'Demander une habilitation'}</h1>
        </div>
      </div>
      {emergency && (
        <div className="alert alert-warn">
          L’accès d’urgence est actif immédiatement, pour 8 heures au plus. Il devra être contrôlé a
          posteriori par une autre personne. Il ne permet de contourner aucune interdiction juridique.
        </div>
      )}
      <form className="card hab-card" onSubmit={submit}>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="user">Bénéficiaire</label>
            <select id="user" required value={form.userId} onChange={(e) => set('userId', e.target.value)}>
              <option value="">- Choisir -</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name} ({KIND_LABELS[u.kind] ?? u.kind})
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="profile">Profil</label>
            <select
              id="profile"
              required
              value={form.profileCode}
              onChange={(e) => {
                set('profileCode', e.target.value);
                set('maxSensitivity', 0);
              }}
            >
              <option value="">- Choisir -</option>
              {refs.profiles.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </select>
            {profile && <span className="hint">{profile.restriction}</span>}
          </div>
          <div className="field">
            <label htmlFor="org">Organisme</label>
            <select
              id="org"
              required
              value={form.organizationId}
              onChange={(e) => set('organizationId', e.target.value)}
            >
              <option value="">- Choisir -</option>
              {refs.organizations
                .filter((o) => o.active)
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="sens">Sensibilité maximale</label>
            <select
              id="sens"
              value={form.maxSensitivity}
              onChange={(e) => set('maxSensitivity', Number(e.target.value))}
            >
              {refs.sensitivities
                .filter((s) => !profile || s.level <= profile.max_sensitivity)
                .map((s) => (
                  <option key={s.level} value={s.level}>
                    {s.level} - {s.label}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <fieldset className="hab-fs">
          <legend>Compétence</legend>
          <div className="hab-scope">
            <label className="checkbox">
              <input
                type="radio"
                name="scope"
                checked={form.scope === 'juridictions'}
                onChange={() => set('scope', 'juridictions')}
              />
              Par juridictions
            </label>
            <label className="checkbox">
              <input
                type="radio"
                name="scope"
                checked={form.scope === 'national'}
                onChange={() => set('scope', 'national')}
              />
              Nationale
            </label>
          </div>
          {form.scope === 'juridictions' && (
            <div className="hab-checks">
              {refs.jurisdictions.map((j) => (
                <label key={j.id} className="checkbox small">
                  <input
                    type="checkbox"
                    checked={form.jurisdictionIds.includes(j.id)}
                    onChange={() => toggle('jurisdictionIds', j.id)}
                  />
                  {j.name}
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <fieldset className="hab-fs">
          <legend>Finalités autorisées</legend>
          <div className="hab-checks">
            {refs.purposes.map((p) => (
              <label key={p.code} className="checkbox small">
                <input
                  type="checkbox"
                  checked={form.purposes.includes(p.code)}
                  onChange={() => toggle('purposes', p.code)}
                />
                {p.label}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="form-grid">
          {emergency ? (
            <div className="field">
              <label htmlFor="duration">Durée (minutes, 480 au plus)</label>
              <input
                id="duration"
                type="number"
                min={15}
                max={480}
                value={form.durationMinutes}
                onChange={(e) => set('durationMinutes', Number(e.target.value))}
              />
            </div>
          ) : (
            <>
              <div className="field">
                <label htmlFor="from">Début</label>
                <input
                  id="from"
                  type="datetime-local"
                  required
                  value={form.validFrom}
                  onChange={(e) => set('validFrom', e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="until">Fin (réexamen au plus tard dans un an)</label>
                <input
                  id="until"
                  type="datetime-local"
                  required
                  value={form.validUntil}
                  onChange={(e) => set('validUntil', e.target.value)}
                />
              </div>
            </>
          )}
        </div>
        <div className="field">
          <label htmlFor="motif">Motif</label>
          <textarea
            id="motif"
            required
            minLength={10}
            value={form.reason}
            onChange={(e) => set('reason', e.target.value)}
          />
          <span className="hint">10 caractères au moins. Le motif est conservé dans l’historique.</span>
        </div>
        <ErrorBox error={error} />
        <div>
          <button className={`btn ${emergency ? 'btn-gold' : ''}`} type="submit" disabled={busy}>
            {busy ? 'Envoi…' : emergency ? 'Accorder l’accès d’urgence' : 'Envoyer la demande'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function UsersPage() {
  const [users, setUsers] = useState<UserSummary[] | null>(null);
  const [form, setForm] = useState({ subject: '', displayName: '', kind: 'agent' });
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () => api<UserSummary[]>('/utilisateurs').then(setUsers).catch(setError);
  useEffect(() => {
    void load();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    try {
      await api('/utilisateurs', { method: 'POST', body: form });
      setNotice('Utilisateur enregistré. Il n’a encore aucun droit : demandez-lui une habilitation.');
      setForm({ subject: '', displayName: '', kind: 'agent' });
      await load();
    } catch (err) {
      setError(err as ApiError);
    }
  };

  return (
    <div className="stack">
      <div className="head hab-head">
        <div>
          <p className="crumb">
            <Link to="/espace/habilitations">← Habilitations</Link>
          </p>
          <h1>Utilisateurs</h1>
        </div>
      </div>
      <form className="card hab-card" onSubmit={submit}>
        <h2>Enregistrer un utilisateur</h2>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="subject">Identifiant de connexion</label>
            <input
              id="subject"
              required
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
            <span className="hint">Identifiant attribué par le fournisseur d’identité.</span>
          </div>
          <div className="field">
            <label htmlFor="name">Nom affiché</label>
            <input
              id="name"
              required
              minLength={2}
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="kind">Type</label>
            <select id="kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="agent">Agent</option>
              <option value="organisme">Organisme</option>
              <option value="technique">Technique</option>
            </select>
          </div>
        </div>
        <ErrorBox error={error} />
        {notice && <p className="alert alert-ok">{notice}</p>}
        <div>
          <button className="btn" type="submit">
            Enregistrer
          </button>
        </div>
      </form>
      <section className="card hab-list">
        <div className="table-wrap tbl-inset">
          <table>
            <thead>
              <tr>
                <th scope="col">Nom</th>
                <th scope="col">Identifiant</th>
                <th scope="col">Type</th>
                <th scope="col">Statut</th>
              </tr>
            </thead>
            <tbody>
              {(users ?? []).map((u) => (
                <tr key={u.id}>
                  <td data-l="Nom">{u.display_name}</td>
                  <td data-l="Identifiant">
                    <code>{u.subject}</code>
                  </td>
                  <td data-l="Type">{KIND_LABELS[u.kind] ?? u.kind}</td>
                  <td data-l="Statut">{u.status === 'active' ? 'Actif' : u.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
